import { useMondayItemManager } from "../components/monday-item-manager-context";
import { useCallback, useEffect, useState } from "react";
import { noop } from "utils/fn-utils";
import { MondayItemData, MondayErrorCodes } from "shared/integrations/monday-types";

type ErrorWithCode = Error & { code: MondayErrorCodes; url?: string };

/**
 * Hook to fetch and subscribe to a Monday item
 * @param itemId - The Monday item ID
 */
export function useMondayItem(itemId: string | undefined): {
  data: MondayItemData | undefined;
  isLoading: boolean;
  error: ErrorWithCode | undefined;
  mutate: (colId: string, colValue: unknown, columnDefinition: { id: string; type: string }) => Promise<void>;
} {
  const context = useMondayItemManager();

  const [value, setValue] = useState<{ data?: MondayItemData; error?: ErrorWithCode } | undefined>(() => {
    if (!itemId) return;
    const item = context.getSingleItem(itemId);
    if (!item) return;
    if (item.error) {
      const error = new Error(item.error.message) as ErrorWithCode;
      error.code = item.error.code;
      return { data: item.data, error };
    }
    return { data: item.data };
  });

  useEffect(() => {
    if (!itemId) return;
    return context.subscribe(itemId, {
      next: (data) => setValue({ data }),
      error: (error: Error) =>
        setValue((previous: { data?: MondayItemData; error?: ErrorWithCode } | undefined) => ({
          data: previous?.data,
          error: error as ErrorWithCode,
        })),
      complete: noop,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const mutate = useCallback(
    (colId: string, colValue: unknown, columnDefinition: { id: string; type: string }) => {
      if (!itemId) return Promise.resolve();

      const boardId = value?.data?.board?.id;
      return context.updateItem(itemId, colId, colValue, columnDefinition, boardId);
    },
    [itemId, value?.data?.board?.id, context]
  );

  return {
    data: value?.data,
    isLoading: !value,
    error: value?.error,
    mutate,
  };
}
